import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ShieldCheckIcon,
  PhoneIcon,
  EnvelopeIcon,
  ClockIcon,
  BuildingStorefrontIcon,
  TruckIcon,
  BriefcaseIcon,
  NoSymbolIcon,
  GlobeAltIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline'

const contactInfo = [
  { key: 'telefono', icon: PhoneIcon, href: 'tel:+528181294348' },
  { key: 'email', icon: EnvelopeIcon, href: 'mailto:avisoprivacidad@visiontec.com.mx' },
  { key: 'horario', icon: ClockIcon },
]

const audiencesData = [
  { key: 'clientes', icon: BuildingStorefrontIcon },
  { key: 'proveedores', icon: TruckIcon },
  { key: 'candidatos', icon: BriefcaseIcon },
]

const rightsData = [
  { key: 'oposicion', icon: NoSymbolIcon },
  { key: 'transferencia', icon: GlobeAltIcon },
  { key: 'consentimiento', icon: CheckCircleIcon },
]

const arcoData = [
  { key: 'revocacion', icon: ArrowPathIcon },
  { key: 'modificaciones', icon: DocumentTextIcon },
  { key: 'aceptacion', icon: ChatBubbleLeftRightIcon },
]

export default function PrivacyNoticePage() {
  const { t } = useTranslation('privacy')
  const [activeAudience, setActiveAudience] = useState(audiencesData[0].key)
  const current = audiencesData.find((a) => a.key === activeAudience)

  return (
    <div className="bg-white">
      {/* Encabezado */}
      <div className="bg-gray-50">
        <div className="mx-auto max-w-6xl px-6 py-16 text-center sm:py-24 lg:px-8">
          <span className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-1.5 text-sm font-medium text-electric-700 shadow-sm">
            <ShieldCheckIcon className="h-4 w-4" aria-hidden="true" />
            {t('header.badge')}
          </span>
          <h1 className="mt-6 text-4xl font-bold tracking-tight text-navy-900 sm:text-5xl">{t('header.title')}</h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-gray-600">
            {t('header.intro')}
          </p>
          <p className="mt-4 text-sm text-gray-400">{t('header.lastUpdated')}</p>
        </div>
      </div>

      {/* Contacto rápido */}
      <div className="mx-auto max-w-6xl px-6 py-12 lg:px-8">
        <div className="rounded-2xl border border-gray-200 bg-gray-50/60 p-6 sm:p-8">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-electric-600">{t('contact.eyebrow')}</h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-600">
            {t('contact.intro')}
          </p>
          <ul className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {contactInfo.map((item) => {
              const content = (
                <>
                  <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-navy-900">
                    <item.icon className="h-5 w-5 text-white" aria-hidden="true" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t(`contact.items.${item.key}.title`)}</p>
                    <p className="mt-0.5 truncate text-sm font-medium text-navy-900">{t(`contact.items.${item.key}.label`)}</p>
                  </div>
                </>
              )
              return (
                <li key={item.key} className="rounded-xl border border-gray-200 bg-white p-4">
                  {item.href ? (
                    <a href={item.href} className="flex items-center gap-3 transition hover:text-electric-600">
                      {content}
                    </a>
                  ) : (
                    <div className="flex items-center gap-3">{content}</div>
                  )}
                </li>
              )
            })}
          </ul>
          <p className="mt-6 text-sm text-gray-600">
            {t('contact.visitPrefix')}{' '}
            <Link to="https://visiontec.com.mx/" target="_blank" rel="noreferrer" className="font-medium text-electric-600 hover:underline">
              www.visiontec.com.mx
            </Link>
            .
          </p>
        </div>
      </div>

      {/* Finalidad por tipo de titular */}
      <div className="mx-auto max-w-6xl px-6 py-12 lg:px-8">
        <h2 className="text-base font-semibold leading-7 text-electric-600">{t('purpose.eyebrow')}</h2>
        <p className="mt-2 text-3xl font-bold tracking-tight text-navy-900">{t('purpose.heading')}</p>
        <p className="mt-4 text-sm leading-6 text-gray-600">
          {t('purpose.description')}
        </p>

        <div className="mt-8 flex flex-wrap gap-2 border-b border-gray-200 pb-2">
          {audiencesData.map((audience) => (
            <button
              key={audience.key}
              type="button"
              onClick={() => setActiveAudience(audience.key)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition ${
                activeAudience === audience.key
                  ? 'bg-navy-900 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <audience.icon className="h-4 w-4" aria-hidden="true" />
              {t(`purpose.audiences.${audience.key}.label`)}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-4 rounded-2xl border border-gray-200 p-6 text-justify sm:p-8">
          {t(`purpose.audiences.${current.key}.paragraphs`, { returnObjects: true }).map((paragraph, index) => (
            <p key={index} className="text-sm leading-6 text-gray-600">
              {paragraph}
            </p>
          ))}
        </div>
      </div>

      {/* Cómo recabamos los datos */}
      <div className="bg-gray-50">
        <div className="mx-auto max-w-6xl px-6 py-12 lg:px-8">
          <h2 className="text-base font-semibold leading-7 text-electric-600">{t('origin.eyebrow')}</h2>
          <p className="mt-2 text-3xl font-bold tracking-tight text-navy-900">{t('origin.heading')}</p>
          <p className="mt-6 text-justify text-sm leading-6 text-gray-600">
            {t('origin.description')}
          </p>
        </div>
      </div>

      {/* Derechos del titular */}
      <div className="mx-auto max-w-6xl px-6 py-12 lg:px-8">
        <h2 className="text-base font-semibold leading-7 text-electric-600">{t('rights.eyebrow')}</h2>
        <p className="mt-2 text-3xl font-bold tracking-tight text-navy-900">{t('rights.heading')}</p>
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
          {rightsData.map((right) => (
            <div key={right.key} className="rounded-2xl border border-gray-200 p-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-navy-900">
                <right.icon className="h-5 w-5 text-white" aria-hidden="true" />
              </div>
              <h3 className="mt-4 text-base font-bold text-navy-900">{t(`rights.items.${right.key}.title`)}</h3>
              <p className="mt-2 text-sm leading-6 text-gray-600">{t(`rights.items.${right.key}.description`)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Derechos ARCO */}
      <div className="bg-navy-950">
        <div className="mx-auto max-w-6xl px-6 py-16 lg:px-8">
          <h2 className="text-base font-semibold leading-7 text-neon-300">{t('arco.eyebrow')}</h2>
          <p className="mt-2 text-3xl font-bold tracking-tight text-white">
            {t('arco.heading')}
          </p>
          <div className="mt-10 space-y-8">
            {arcoData.map((item) => (
              <div key={item.key} className="flex gap-4">
                <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-white/10">
                  <item.icon className="h-5 w-5 text-neon-300" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">{t(`arco.items.${item.key}.title`)}</h3>
                  <p className="mt-2 text-sm leading-6 text-white/70">{t(`arco.items.${item.key}.description`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Cierre */}
      <div className="mx-auto max-w-6xl px-6 py-12 text-center lg:px-8">
        <p className="text-sm text-gray-500">
          {t('closing.question')}{' '}
          <Link to="/contact" className="font-medium text-electric-600 hover:underline">
            {t('closing.cta')}
          </Link>
          .
        </p>
      </div>
    </div>
  )
}
